import { AfterContentInit, ChangeDetectorRef, Component, EventEmitter, inject, NgZone, OnDestroy, OnInit, Output, signal, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSelectionListChange } from '@angular/material/list';
import { ActivatedRoute } from '@angular/router';
import { EnpsSinglePageComponent } from '@common/components/base/enps-single-page';
import { DataStore } from '@common/data-models/data-store/data-store';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { UserGroup } from '@common/data-models/search-results/result-group';
import { UsersResults } from '@common/data-models/search-results/users-result';
import { UserStaffInfo } from '@common/data-models/service/user-staff-info';
import { AuthService } from '@common/services/authentication/auth.service';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { NavigationService } from '@common/services/navigation-service';
import { TeamMembersService } from '@common/services/team-members-service';
import { GlobalRoles, InvitationStatusCodes } from '@common/utils/util';
import { Store } from '@ngrx/store';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { NavigationDirection } from '@src/suite/common/enums/navigation-direction';
import { ViewStateActions } from '@src/suite/common/store/navigation/actions/viewstate.actions';
import { AppService } from '@suite/app.service';
import { VisualStateService } from '@suite/common/services/visual-state-service';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

import { AddTeamMemberComponent } from './team-member-item/add-team-member';

@Component({
    selector: 'st-manage-team-members',
    templateUrl: './manage-team-members.html',
    styleUrls: ['./manage-team-members.scss', '../base/enps-single-page.scss'],
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-manage-team-members',
    },
})
export class ManageTeamMembersComponent extends EnpsSinglePageComponent implements OnInit, OnDestroy, AfterContentInit {
    private store = inject(Store);
    loading = signal(false);

    public predefineFilters: { [id: string]: Array<{ id: string; title: string }> };
    public readonly PSTFieldNames = PSTFieldNames;
    public currentRole;
    @Output() public changed: EventEmitter<UserStaffInfo | string> = new EventEmitter<any>();

    public roles;
    public programs = [];

    public get groupByField(): string {
        return UsersResults.groupByField;
    }

    public get sortByField(): any {
        return UsersResults.sortByField;
    }
    public set sortByField(v: any) {
        if (v !== UsersResults.sortByField) {
            setTimeout(
                () => {
                    UsersResults.sortByField = v;
                    if (DataStore.usersResults.users.length > 0 || DataStore.usersResults.newUsers.length > 0) {
                        DataStore.usersResults.sortGroups(v);
                        DataStore.usersResults.rebuildGroups(this.groupByField, this.sortByField);
                    }
                },
                0,
                this.changeDetectorRef
            );
        }
    }
    public InvitationStatusCodes: any = InvitationStatusCodes;
    public currentTabIndex: number = 0;
    public currentGroup: string = '';

    @ViewChild('mainCta', { read: TemplateRef, static: true }) mainCta;
    @ViewChild('viewOptions', { read: TemplateRef, static: true }) viewOptions;
    @ViewChild('rightSlot', { read: TemplateRef, static: true }) rightSlot;
    public displayedColumns: string[] = ['Avatar', 'ID', 'First Name', 'Last Name', 'Email', 'SSO Logins', 'Default Team', 'Role', 'Status', 'Invitation Status', 'Delete'];
    public teamMemberDialogRef: MatDialogRef<AddTeamMemberComponent, any> = null;

    pageSize = 20;
    pageIndex = 0;
    hidePageSize = true;
    showFirstLastButtons = true;
    NavigationDirection = NavigationDirection;

    constructor(
        public appService: AppService,
        protected modalService: ModalService,
        protected ngZone: NgZone,
        protected changeDetectorRef: ChangeDetectorRef,
        authService: AuthService,
        protected activatedRoute: ActivatedRoute,
        public globalTablesService: GlobalTablesService,
        public navigationService: NavigationService,
        public languageSettings: LanguageSettings,
        public teamMembersService: TeamMembersService,
        public visualStateService: VisualStateService,
        private dialog: MatDialog
    ) {
        super(appService, modalService, ngZone, changeDetectorRef, authService, activatedRoute, globalTablesService, navigationService, languageSettings);
        this.predefineFilters = {
            grouping: [
                { id: PSTFieldNames.UserInfoStatus, title: this.languageSettings.PSTWebUserGroupSortByStatusLabel },
                { id: PSTFieldNames.UserInfoProgram, title: this.languageSettings.PSTWebUserGroupSortByProgramLabel },
                { id: PSTFieldNames.UserInfoRole, title: this.languageSettings.PSTWebUserGroupSortByRoleLabel },
            ],
            sorting: [
                { id: PSTFieldNames.UserInfoID, title: this.languageSettings.PSTWebUserGroupSortByIDLabel },
                { id: PSTFieldNames.UserInfoProgram, title: this.languageSettings.PSTWebUserGroupSortByProgramLabel },
                { id: PSTFieldNames.UserInfoGivenName, title: this.languageSettings.PSTWebUserGroupSortByFirstNameLabel },
                { id: PSTFieldNames.UserInfoSurname, title: this.languageSettings.PSTWebUserGroupSortByLastNameLabel },
                { id: PSTFieldNames.UserInfoRole, title: this.languageSettings.PSTWebUserGroupSortByRoleLabel },
                { id: PSTFieldNames.UserInfoEmail, title: this.languageSettings.PSTWebUserGroupSortByEmailLabel },
                { id: PSTFieldNames.UserInfoStatus, title: this.languageSettings.PSTWebUserGroupSortByStatusLabel },
            ],
        };
    }

    public async ngOnInit() {
        this.generateProgramsArray();
        this.navigationService.currentListView = this;
    }

    ngOnDestroy() {
        super.ngOnDestroy();
        DataStore.usersResults.dispose();
    }

    ngAfterContentInit(): void {
        this.store.dispatch(ViewStateActions.updateTemplateRefs({ mainCta: this.mainCta, secondaryCta: null, viewOptions: this.viewOptions, rightSlot: this.rightSlot }));
    }

    private async loadTeamMembersData() {
        this.loading.set(true);
        const res = await this.teamMembersService.getTeamMembers();

        if (res.status) {
            DataStore.usersResults = res.data.userResults;
            DataStore.usersResults.rebuildGroups(this.groupByField, this.sortByField);
            this.proccessSecurity(res.data.security);
            this.setCurrentRole(res.data.userResults.newUsers);
            this.setCurrentRole(res.data.userResults.users);
            this.loadCurrentGroup();
        }
        this.loading.set(false);
    }

    async openNewMemberDialog() {
        this.dialog.closeAll();
        await firstValueFrom(this.dialog.afterAllClosed.pipe(take(1)));
        this.teamMemberDialogRef = this.dialog.open(AddTeamMemberComponent, {
            width: '35rem',
            hasBackdrop: true,
            panelClass: 'st-add-team-member-dialog',
            data: { data: this.roles },
        });
    }

    private proccessSecurity(security: Array<any>) {
        if (security) {
            const roles = JSON.parse(JSON.stringify(security));
            roles.unshift(GlobalRoles.SUPERVISOR_ITEM);
            roles.unshift(GlobalRoles.SERVER_MANAGER_ITEM);
            roles.unshift(GlobalRoles.RESTRICTED_USER_ITEM);

            this.roles = roles;
        }
    }

    private setCurrentRole(users: Array<UserStaffInfo>) {
        users.forEach(user => {
            if (user.Supervisor) {
                user.GlobalRole = GlobalRoles.SUPERVISOR_ID;
            }
        });
    }

    private loadCurrentGroup() {
        const entries = this.userGroups[this.groupByField]?.keys;
        this.currentTabIndex = 0;
        this.currentGroup = entries[this.currentTabIndex]?.value;
    }

    get pageIndexToShow(): number {
        return this.pageIndex + 1;
    }

    totalCount(group: string) {
        return this.userGroups[this.groupByField]?.getPagesCount(group);
    }

    pages(group: string) {
        return this.userGroups[this.groupByField]?.getPages(group);
    }

    private generateProgramsArray() {
        this.programs = [];
        this.loadTeamMembersData();
        if (this.globalTablesService.Groups) {
            for (const program in this.globalTablesService.Groups) {
                this.programs.push(this.globalTablesService.Groups[program]);
            }
        }
    }

    public get userGroups(): { [id: string]: UserGroup<UserStaffInfo> } {
        return DataStore.usersResults?.usersGroups;
    }

    public onPageChanged(group: string, event: MatSelectionListChange) {
        this.pageIndex = event.options[0].value;
        this.userGroups[this.groupByField].setCurrentPage(group, this.pageIndex);
    }

    public onTabChanged(event: any) {
        const entries = this.userGroups[this.groupByField]?.keys;
        this.currentTabIndex = event.index;
        this.currentGroup = entries[this.currentTabIndex]?.value;
    }

    public async deleteItem(member: UserStaffInfo) {
        const modalRef = this.modalService.openModal(this.languageSettings.PSTWebConfirmDeleteUserLabel, this.languageSettings.PSTWebConfirmDeleteUserMessage + member.UserID, 'Yes', 'No');
        let modalRes;
        try {
            modalRes = await firstValueFrom(modalRef.result);
        } catch (er) {
            modalRes = ModalResponseCodes.CANCEL;
        }
        if (modalRes === ModalResponseCodes.OK) {
            try {
                this.loading.set(true);
                const res = await this.teamMembersService.removeTeamMember(member.UserID);
                if (res.status) {
                    DataStore.usersResults.removeUser(member);
                    DataStore.usersResults.rebuildGroups(UsersResults.groupByField, UsersResults.sortByField);
                    this.loadCurrentGroup();
                }
                this.loading.set(false);
            } catch (e) {
                this.loading.set(false);
            }
        } else {
            console.log('no');
        }
    }

    public async sendInvitation(member: UserStaffInfo) {
        const res = await this.teamMembersService.inviteTeamMember(member.UserID, member.Email);
        if (res.status) {
            member.InvitationStatus = InvitationStatusCodes.PENDING;
            DataStore.usersResults.removeUser(member);
            DataStore.usersResults.addUser(member);
            DataStore.usersResults.rebuildGroups(UsersResults.groupByField, UsersResults.sortByField);
        }
    }

    public async saveMember(member: UserStaffInfo) {
        try {
            const res = await this.teamMembersService.updateTeamMember(member);
            if (res.status) {
                member = Object.assign(new UserStaffInfo(), res.data);
                if (this.appService.nomWebServicesJson.HelloResults.UserStaffInfo.UserID === member.UserID) {
                    this.appService.nomWebServicesJson.HelloResults.UserStaffInfo = Object.assign(new UserStaffInfo(), member);
                }
                DataStore.usersResults.updateUser(member);
                DataStore.usersResults.rebuildGroups(UsersResults.groupByField, UsersResults.sortByField);
            } else {
                // this.restoreState();
            }
        } catch (e) {
            // this.restoreState();
        }
    }

    valueChanged(member: UserStaffInfo, value: any, prop?: string) {
        this.changed.emit(prop);
        if (prop) {
            member[prop] = value;
        }
        this.saveMember(member);
    }

    public selectRole(member: UserStaffInfo) {
        if (member.GlobalRole === GlobalRoles.SUPERVISOR_ID) {
            member.Supervisor = true;
        } else {
            member.Supervisor = false;
            member.ServerManager = false;
            // member.GlobalRole = this.currentRole.ID;
        }
        this.saveMember(member);
    }

    public invitationButtonLabel(member: UserStaffInfo) {
        if (member.InvitationStatus === InvitationStatusCodes.ACTIVE) {
            return this.languageSettings.PSTWebSendedInvitationButtonLabel;
        }
        return this.languageSettings.PSTWebSendInvitationButtonLabel;
    }

    public navigate(direction: NavigationDirection, group?: string) {
        switch (direction) {
            case NavigationDirection.FIRST:
                this.pageIndex = 0;
                this.userGroups[this.groupByField].setCurrentPage(group, this.pageIndex);
                break;
            case NavigationDirection.PREVIOUS:
                if (this.pageIndex > 0) {
                    this.pageIndex = this.pageIndex - 1;
                } else {
                    this.pageIndex = this.totalCount(group) - 1;
                }
                this.userGroups[this.groupByField].setCurrentPage(group, this.pageIndex);
                break;
            case NavigationDirection.NEXT:
                if (this.pageIndex < this.totalCount(group) - 1) {
                    this.pageIndex = this.pageIndex + 1;
                } else {
                    this.pageIndex = 0;
                }
                this.userGroups[this.groupByField].setCurrentPage(group, this.pageIndex);
                break;
            case NavigationDirection.LAST:
                this.pageIndex = this.totalCount(group) - 1;
                this.userGroups[this.groupByField].setCurrentPage(group, this.pageIndex);
                break;
        }
    }
}
